import { Component, DestroyRef, inject, Input, OnDestroy } from '@angular/core';
import { faBars, faBook, faChartPie, faCircleQuestion, faClipboardQuestion, faKey, faSliders } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { Rights } from 'src/app/models/server/enums/rights';
import { IAuthenticate } from 'src/app/models/server/interface/authenticate.interface';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { ProfileSettingsService } from 'src/app/services/http/profile-settings.service';
import { SettingsService } from 'src/app/services/http/settings.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { ProfileSettingsStateService } from 'src/app/services/state/profile-settings-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';

import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DialogProfileSettingsData } from '@hub4b/components/dialogs/profile-settings/models/dialog-profile-settings-data';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { EnumModeResetPassword } from 'src/app/models/server/enums/enum-mode-reset-password';
import { EnumReasonResetPassword } from 'src/app/models/server/enums/enum-reason-reset-password';
import { ServiceCodes } from 'src/app/models/server/enums/service-codes';
import { ExternalParameters } from 'src/app/models/server/external-parameters';
import { UsersService } from 'src/app/services/http/users.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-banner-menu',
    templateUrl: './banner-menu.component.html',
    styleUrls: ['./banner-menu.component.scss'],
    imports: [MatTooltipModule, MatMenuModule, FaIconComponent]
})
export class BannerMenuComponent implements OnDestroy {
    private toastrService = inject(ToastrService);
    private spinner = inject(NgxSpinnerService);
    private connectedUsersStateService = inject(ConnectedUsersStateService);
    private destroyRef = inject(DestroyRef);
    private usersService = inject(UsersService);

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    public menuIcon = faBars;
    public profilIcon = faSliders;
    public dashboardIcon = faChartPie;
    public handbookIcon = faBook;
    public faqIcon = faClipboardQuestion;
    public aboutIcon = faCircleQuestion;
    public faKey = faKey;

    @Input()
    public userConnected: IAuthenticate;

    public userManagerUrl: string;
    public backOfficeUrl: string;

    public supervisionRight: boolean = false;
    public editProfilRight: boolean = false;
    public supervisionSubscription: boolean = false;

    constructor(
        private settingsService: SettingsService,
        private profileSettingsService: ProfileSettingsService,
        private profileSettingsStateService: ProfileSettingsStateService,
        private connectedUserStateService: ConnectedUsersStateService,
        private dialogService: DialogService,
        private viewsStateService: ViewsStateService
    ) {
        this.settingsService
            .getSettings()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                this.userManagerUrl = result.apps['gum'].url;
                this.backOfficeUrl = result.apps['hub4b'].backoffice;
            });

        this.supervisionRight = this.connectedUserStateService.hasRight(Rights.Supervision);
        this.editProfilRight = this.connectedUserStateService.hasRight(Rights.ProfileEdit);
        this.supervisionSubscription = this.connectedUserStateService.hasServiceSubscription(ServiceCodes.HubDashBoard);
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Affichage d'une popup pour les informations générale du produit.
     */
    public openAbout(): void {
        this.dialogService.openAbout().subscribe();
    }

    /**
     * Ouverture de la gestion des utilisateurs dans un nouvel onglet.
     */
    public openGroupManager() {
        window.open(`${this.userManagerUrl}/groups?__access_token__=${this.userConnected.token.accessToken}`);
    }

    /**
     * Ouverture de la gestion des utilisateurs dans un nouvel onglet.
     */
    public openUserManager() {
        window.open(`${this.userManagerUrl}/users?__access_token__=${this.userConnected.token.accessToken}`);
    }

    /**
     * Ouverture du backoffice de Hub4B dans un nouvel onglet.
     */
    public openBackOffice() {
        window.open(`${this.backOfficeUrl}/organizationSelect?__access_token__=${this.userConnected.token.accessToken}`);
    }

    /**
     * Affiche la popup de supervision.
     */
    public openSupervision(): void {
        this.dialogService.openSupervision().subscribe();
    }

    /**
     * Affiche la popup du profil utilisateur.
     */
    public openProfileSettings(): void {
        this.dialogService.openProfileSettings().subscribe((data: DialogProfileSettingsData) => {
            if (!!data) {
                this.profileSettingsService.update(data.settings).subscribe(() => {
                    this.profileSettingsStateService.setProfileSettings(data.settings);

                    if (data.needViewReloading === true) {
                        // Emet un evenement de changement de vue pour recharger la configuration de la vue.
                        this.viewsStateService.setSelectedView(this.viewsStateService.getSelectedView(), true);
                    }
                });
            }
        });
    }

    modifyPassword() {
        if (this.connectedUsersStateService.getUserConnected().organization.officeDomain) {
            this.toastrService.warning('Veuillez-vous rapprocher de votre service informatique pour la modification de votre mot de passe Microsoft.');
        } else {
            this.spinner.show();

            this.settingsService
                .getSettings()
                .pipe(
                    switchMap((x) => {
                        let gumUrl: string;
                        try {
                            gumUrl = x.apps['gum']['url'];
                        } catch {
                            gumUrl = null;
                        }

                        if (!environment.production && gumUrl === null) {
                            gumUrl = 'http://10.20.0.10:9030';
                        }

                        if (gumUrl) {
                            let parameters = new ExternalParameters();
                            parameters.name = 'Hub4B - Back Office';
                            parameters.return = encodeURIComponent(`${window.location.origin}`);
                            let redirectUrl = encodeURIComponent(
                                `${gumUrl}/changepassword?resetPassword=${this.connectedUsersStateService.getUserConnected().userId}&parameters=${JSON.stringify(parameters)}`
                            );

                            return this.usersService.StartPasswordReset(
                                this.connectedUsersStateService.getUserConnected().userId,
                                EnumReasonResetPassword.Modify,
                                EnumModeResetPassword.Mail,
                                redirectUrl
                            );
                        } else {
                            throw 'Domain Error';
                        }
                    }),
                    catchError((err) => {
                        throw err;
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: () => {
                        this.spinner.hide();

                        const emailContact = this.connectedUsersStateService.getUserConnected().login;

                        this.toastrService.success(
                            `Un message a été envoyé à \"${emailContact}\" pour valider la réinitialisation du mot de passe.<br /><br />En cas de non réception vérifier votre adresse mail ou les courriers indésirables.`,
                            '',
                            {
                                enableHtml: true,
                            }
                        );
                    },
                    error: (err: unknown) => {
                        this.spinner.hide();

                        if (err === 'Domain Error') {
                            this.toastrService.warning("Votre domaine n'est pas correctement configuré.<br />Veuillez contacter votre administrateur.", '', { enableHtml: true });
                        } else {
                            const error = err as HttpErrorResponse;

                            if (error.status === 412) {
                                this.toastrService.error(
                                    "L'envoi de mail pour le changement de mot de passe ne peut pas être effectué. Veuillez contacter votre administrateur.",
                                    '',
                                    { enableHtml: true }
                                );
                            } else {
                                this.toastrService.error("Erreur lors de l'envoi de mail pour le changement de mot de passe. Veuillez contacter votre administrateur.", '', {
                                    enableHtml: true,
                                });
                            }
                        }
                    },
                });
        }
    }
}
